"use client";

import { useCallback } from "react";

import { SSOSuccessHandlerOptions, SSOSuccessHandlerResult } from "../types";
import { PostLoginResponse } from "@/interface/api/postLogin/types";
import { PostRegisterResponse } from "@/interface/api/postRegister/types";

/**
 * Hook that handles successful SSO authentication responses.
 * Extracts tokens, saves cookies, and notifies completion.
 */
export const useSSOSuccessHandler = ({
  onSaveCookie,
  onSsoCompleted,
  provider,
  registerSsoCredential,
}: SSOSuccessHandlerOptions): SSOSuccessHandlerResult => {
  const handleLoginSuccess = useCallback(
    (response: PostLoginResponse) => {
      if (response?.status === 200) {
        const token = response?.data?.token;
        const tokenExpiration = response?.data?.expiration;

        if (token && tokenExpiration) {
          onSaveCookie(token, tokenExpiration);
          onSsoCompleted?.(provider, response);
        }
      }
    },
    [onSaveCookie, onSsoCompleted, provider]
  );

  const handleRegisterSuccess = useCallback(
    (response: PostRegisterResponse) => {
      if (response?.status === 201) {
        const token = response?.data?.auth?.token;
        const tokenExpiration = response?.data?.auth?.expiration;

        if (token && tokenExpiration) {
          onSaveCookie(token, tokenExpiration);
          onSsoCompleted?.(provider, response, {
            registerSsoToken: registerSsoCredential,
          });
        }
      }
    },
    [onSaveCookie, onSsoCompleted, provider, registerSsoCredential]
  );

  return {
    handleLoginSuccess,
    handleRegisterSuccess,
  };
};
